import type { ReactNode } from "react"
import { useCallback, useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import {
	IconBellFill18,
	IconShapesFill18,
	IconUserFill18,
} from "nucleo-ui-fill-18"
import { IconCheckOutline18 as Check } from "nucleo-ui-outline-18"

import type { NestedCommandGroup } from "@productlane/ui/src/components/internal/nested-command"
import {
	NOTIFICATION_REASONS,
	NOTIFICATION_TYPE_COPY,
	notificationTypeKeys,
} from "@productlane/lib/src/browser"
import { cn } from "@productlane/lib/src/cn"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"
import { queries } from "@productlane/zero/queries"

import type { NotificationFilterKey } from "./notification-filter-state"
import type { FilterChip, SearchableFilterItem } from "@/components/filters"
import {
	FilterControlChips,
	FilterPopover,
	useFilteredSearchItems,
} from "@/components/filters"
import { ActorAvatar, SystemActorAvatar } from "./actor-avatar"
import { NOTIFICATIONS_COPY as copy } from "./copy"
import {
	SYSTEM_ACTOR,
	useNotificationFilterState,
} from "./notification-filter-state"
import { notificationTypeIcon } from "./notification-icons"

interface FilterOption {
	value: string
	label: string
	icon: ReactNode
}

const CATEGORIES: NestedCommandGroup<NotificationFilterKey>[] = [
	{ type: "types", label: copy.filters.type, Icon: IconShapesFill18 },
	{ type: "reasons", label: copy.filters.reason, Icon: IconBellFill18 },
	{ type: "actorIds", label: copy.filters.from, Icon: IconUserFill18 },
]

const CATEGORY_LABELS: Record<NotificationFilterKey, string> = {
	types: copy.filters.type,
	reasons: copy.filters.reason,
	actorIds: copy.filters.from,
}

const iconClass = "h-3.5 w-3.5 flex-none text-secondary"

const TYPE_OPTIONS: FilterOption[] = notificationTypeKeys().map((type) => {
	const Icon = notificationTypeIcon(type)
	return {
		value: type,
		label: NOTIFICATION_TYPE_COPY[type]?.label ?? type,
		icon: <Icon className={iconClass} />,
	}
})

const REASON_OPTIONS: FilterOption[] = NOTIFICATION_REASONS.map((reason) => ({
	value: reason,
	label: copy.reasons[reason] ?? reason,
	icon: null,
}))

function useFilterOptions(): Record<NotificationFilterKey, FilterOption[]> {
	const [users] = useQuery(queries.users())
	return useMemo(
		() => ({
			types: TYPE_OPTIONS,
			reasons: REASON_OPTIONS,
			actorIds: [
				{
					value: SYSTEM_ACTOR,
					label: copy.filters.system,
					icon: <SystemActorAvatar size="xs" />,
				},
				...users.map((user) => ({
					value: user.id,
					label: user.name ?? user.email,
					icon: <ActorAvatar user={user} size="xs" />,
				})),
			],
		}),
		[users],
	)
}

function FilterOptionItems({
	filterKey,
	options,
	onSelect,
}: {
	filterKey: NotificationFilterKey
	options: FilterOption[]
	onSelect: () => void
}) {
	const selected = useNotificationFilterState((s) => s[filterKey])
	const toggle = useNotificationFilterState((s) => s.toggle)
	return (
		<>
			{options.map((option) => {
				const isSelected = selected.includes(option.value)
				return (
					<CommandItem
						key={option.value}
						value={`${option.label} ${option.value}`}
						onSelect={() => {
							toggle(filterKey, option.value)
							onSelect()
						}}
					>
						<div className="flex w-full items-center gap-2">
							{option.icon}
							<span className="flex-1 truncate">{option.label}</span>
							<Check
								className={cn(
									"h-4 w-4 flex-none",
									isSelected ? "opacity-100" : "opacity-0",
								)}
							/>
						</div>
					</CommandItem>
				)
			})}
		</>
	)
}

function FilterOptionsPanel({
	filterKey,
	options,
	query,
	setQuery,
	onSelect,
}: {
	filterKey: NotificationFilterKey
	options: FilterOption[]
	query: string
	setQuery: (q: string) => void
	onSelect: () => void
}) {
	return (
		<Command>
			<CommandInput
				key={filterKey}
				autoFocus
				placeholder="Filter..."
				value={query}
				onValueChange={setQuery}
			/>
			<CommandList className="max-h-[min(70dvh,320px)]">
				<CommandEmpty>No results found.</CommandEmpty>
				<CommandGroup>
					<FilterOptionItems
						filterKey={filterKey}
						options={options}
						onSelect={onSelect}
					/>
				</CommandGroup>
			</CommandList>
		</Command>
	)
}

export function NotificationFilter() {
	const [globalQuery, setGlobalQuery] = useState("")
	const options = useFilterOptions()
	const state = useNotificationFilterState()

	const counts = useMemo(
		() => ({
			types: state.types.length,
			reasons: state.reasons.length,
			actorIds: state.actorIds.length,
		}),
		[state.types, state.reasons, state.actorIds],
	)

	const searchableItems = useMemo(() => {
		const items: SearchableFilterItem<NotificationFilterKey>[] = []
		for (const category of CATEGORIES) {
			for (const option of options[category.type]) {
				items.push({
					id: `${category.type}-${option.value}`,
					category: category.type,
					categoryLabel: category.label,
					label: option.label,
					searchText: `${category.label} ${option.label}`,
					onSelect: () => state.toggle(category.type, option.value),
					isSelected: state[category.type].includes(option.value),
				})
			}
		}
		return items
	}, [options, state])
	const filteredSearchItems = useFilteredSearchItems(
		globalQuery,
		searchableItems,
	)

	const renderSubPanel = useCallback(
		(
			group: NotificationFilterKey,
			query: string,
			setQuery: (q: string) => void,
			onFilterItemSelect: () => void,
		) => (
			<FilterOptionsPanel
				filterKey={group}
				options={options[group]}
				query={query}
				setQuery={setQuery}
				onSelect={onFilterItemSelect}
			/>
		),
		[options],
	)

	return (
		<FilterPopover<NotificationFilterKey>
			categories={CATEGORIES}
			counts={counts}
			renderSubPanel={renderSubPanel}
			searchableItems={searchableItems}
			filteredSearchItems={filteredSearchItems}
			onGlobalQueryChange={setGlobalQuery}
			// The list sits against the sidebar, so a left sub-panel runs off screen.
			subPanelSide="right"
		/>
	)
}

export function NotificationFilterChips() {
	const options = useFilterOptions()
	const state = useNotificationFilterState()
	const [query, setQuery] = useState("")

	const chips = useMemo<FilterChip[]>(() => {
		const keys: NotificationFilterKey[] = ["types", "reasons", "actorIds"]
		return keys.flatMap((key) => {
			const values = state[key]
			if (values.length === 0) return []
			const first = options[key].find((o) => o.value === values[0])
			return [
				{
					key,
					label: CATEGORY_LABELS[key],
					middleText: values.length > 1 ? "is any of" : "is",
					valueText:
						values.length === 1
							? (first?.label ?? values[0]!)
							: copy.filters.selectedCount[key](values.length),
					onClear: () => state.clear(key),
					popoverContent: (
						<FilterOptionsPanel
							filterKey={key}
							options={options[key]}
							query={query}
							setQuery={setQuery}
							onSelect={() => setQuery("")}
						/>
					),
				},
			]
		})
	}, [options, state, query])

	return <FilterControlChips chips={chips} />
}
